/**
 * src/lib/seo.js — dữ liệu <head> cho từng đường dẫn.
 *
 * Dùng ở hai nơi và phải cho ra kết quả giống hệt nhau:
 *  - scripts/prerender.mjs, lúc build, để nhúng thẳng vào file HTML tĩnh
 *  - (sau này) khi điều hướng trong app, để cập nhật title
 *
 * Vì thế nó là hàm thuần, không đụng tới `document`.
 */

import { TOOLS, toolBySlug, SO_CONG_CU } from '../data/tools.js';
import { contentFor } from '../data/toolContent.js';
import { LEGAL_PAGES, legalBySlug } from '../data/legalContent.js';
import { DA_XUAT_BAN, tuDienTheoSlug, CUM } from '../data/glossary.js';
import { anhChiaSeThuatNgu } from '../data/glossaryImages.js';
import { anhChiaSeCongCu } from '../data/toolImages.js';
import { anhChiaSeMau } from '../data/templateImages.js';
import { anhChiaSeQuy } from '../data/fundImages.js';
import { PROGRAMS } from '../data/programs.js';
import { noiDungTheoSlug } from '../data/glossaryContent.js';
import { boLienKet } from './lienKetChu.js';
import { FUNDS } from '../data/funds.js';
import { PLAYBOOK_POSTS, PLAYBOOK_CATEGORIES, playbookBySlug } from '../data/playbook.js';
import { categoryBySlug, categorySlug, ORG_AUTHOR, TRACKS } from './playbookModel.js';
import { TEMPLATES, templateBySlug } from '../data/templates.js';
import { DINH_DANG } from './templateModel.js';
import { TRAM } from '../data/lo-trinh.js';
import { VIDEOS, videoCuaTrang } from '../data/videos.js';
import { videoObject } from '@shared/video.js';

export const SITE_NAME = 'Bộ công cụ gọi vốn';

/** Rút gọn về khoảng 155 ký tự — quá dài Google sẽ tự cắt giữa câu. */
// Tiêu đề và mô tả riêng cho vài trang Từ điển, viết theo đúng cách người
// đọc gõ trên Google (theo Search Console 28/09 đến 25/10/2026).
// Trang không có trong bảng này dùng tiêu đề tự sinh như cũ.
const SEO_THUAT_NGU = {
  'vong-pre-seed': {
    title: 'Pre-seed là gì? Vòng pre-seed của startup gọi bao nhiêu, ai đầu tư',
    description: 'Pre-seed là vòng gọi vốn đầu tiên có tổ chức, thường khi đã có sản phẩm chạy được nhưng chưa có doanh thu đáng kể. Ai thường đầu tư vòng này, gọi bao nhiêu là hợp lý và khác vòng seed thế nào.',
  },
  'vesting-la-gi': {
    title: 'Vesting là gì? Cổ phần nhận dần, cliff và ví dụ cho founder',
    description: 'Vesting là cơ chế nhận cổ phần dần theo thời gian làm việc thay vì nhận hết ngay. Cách tính lịch vesting phổ biến (4 năm, cliff 1 năm), vì sao nhà đầu tư yêu cầu và ví dụ bằng số.',
  },
  'cliff-vesting': {
    title: 'Cliff là gì? Cliff trong vesting cổ phần founder, ví dụ bằng số',
    description: 'Cliff là khoảng thời gian đầu của lịch vesting mà người nhận chưa sở hữu cổ phần nào; đủ mốc cliff mới nhận trọn phần đã tích luỹ. Ví dụ bằng số, cliff sinh ra để làm gì và sai lầm thường gặp.',
  },
  'pitch-deck-la-gi': {
    title: 'Pitch deck là gì? Slide deck gọi vốn gồm 10 slide nào',
    description: 'Pitch deck (slide deck, deck gọi vốn) là bộ slide giới thiệu doanh nghiệp gửi nhà đầu tư, thường 10 đến 15 trang. Mười slide cốt lõi, câu hỏi mỗi slide phải trả lời, ví dụ viết tốt và cách tự kiểm tra trước khi gửi.',
  },
  'ltv-la-gi': {
    title: 'LTV là gì? Cách tính giá trị vòng đời khách hàng (LTV) cho startup',
    description: 'LTV là tổng lãi gộp một khách hàng mang lại trong suốt thời gian còn dùng sản phẩm. Công thức tính LTV, ví dụ bằng số và cách so với CAC trước khi gọi vốn.',
  },
  'mrr-arr': {
    title: 'MRR là gì? MRR, ARR và cách tính cho startup SaaS',
    description: 'MRR là doanh thu định kỳ hàng tháng, ARR là MRR nhân 12. Chỉ tính phần thật sự lặp lại. Ví dụ: 27 khách, mỗi khách 2 triệu một tháng, MRR 54 triệu, ARR 648 triệu; bốn thành phần nên tách riêng và sai lầm thường gặp.',
  },
  'bien-loi-nhuan-gop': {
    title: 'Biên lợi nhuận gộp là gì? Công thức tính và ví dụ cho startup',
    description: 'Biên lợi nhuận gộp là phần trăm doanh thu còn lại sau khi trừ chi phí trực tiếp: biên lợi nhuận gộp = lợi nhuận gộp / doanh thu (doanh thu 300 triệu, lãi gộp 210 triệu thì biên 70%).',
  },
  'vong-seed': {
    title: 'Vòng seed là gì? Seed khác pre-seed, Series A thế nào',
    description: 'Seed là vòng gọi vốn sau pre-seed, khi startup đã có bằng chứng thị trường rõ ràng và doanh thu đang tăng. Thứ tự các vòng gọi vốn (pre-seed, seed, Series A), cột mốc cần đạt và sai lầm thường gặp.',
  },
  'trien-khai-thu': {
    title: 'Pilot là gì? Triển khai thử (pilot) với khách hàng doanh nghiệp',
    description: 'Pilot (triển khai thử) là cho khách hàng dùng sản phẩm trong phạm vi và thời gian có kiểm soát, để hai bên đánh giá trước khi ký hợp đồng chính thức. Vì sao pilot có thu phí đáng giá hơn, cách thiết kế và sai lầm thường gặp.',
  },
  'khau-vi-dau-tu': {
    title: 'Khẩu vị đầu tư (investment thesis) là gì? Cách đọc khẩu vị của quỹ',
    description: 'Khẩu vị đầu tư (investment thesis) là bộ tiêu chí một quỹ dùng để quyết định xem xét hồ sơ nào: ngành, giai đoạn, địa bàn và quy mô séc. Cách đọc khẩu vị của quỹ trước khi gửi hồ sơ, và khi tuyên bố khác thực tế.',
  },
};

function tenTieuDe(muc) {
  const phu = muc.aliases?.[0];
  // Chỉ thêm tên tiếng Anh ngắn, một cụm; tên mục dạng câu hỏi ("Vì sao…",
  // "Gọi bao nhiêu…") thêm ngoặc vào chỉ làm tiêu đề khó đọc.
  if (!phu || phu.length > 24 || phu.includes(',') || /^(Vì sao|Chọn|Gọi bao nhiêu|Bị )/.test(muc.term)) return muc.term;
  const chuan = (x) => x.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  // Bỏ qua khi tên đã có ngoặc, hoặc tên tiếng Anh bắt đầu bằng chữ đã có
  // ("Vòng pre-seed" với "pre-seed round"): thêm vào chỉ làm tiêu đề lủng củng.
  const tu = chuan(muc.term);
  const lap = tu.includes(chuan(phu).split(/\s+/)[0]);
  return muc.term.includes('(') || tu.includes(chuan(phu)) || lap ? muc.term : `${muc.term} (${phu})`;
}

function tomTat(s, max = 155) {
  const t = s.replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  return `${cut.slice(0, cut.lastIndexOf(' '))}…`;
}

/**
 * Metadata của trang, cộng thêm VideoObject cho mỗi video gắn vào trang đó
 * (khối "Xem nhanh"), để Google có thể hiện trang kèm ô video.
 */
export function metaFor(path, host = '') {
  const meta = metaGoc(path, host);
  if (meta.noindex) return meta;
  const ds = videoCuaTrang(path === '/' ? '/' : path.replace(/\/+$/, ''));
  return ds.length ? { ...meta, jsonLd: [...(meta.jsonLd || []), ...ds.map((v) => videoObject(v, host))] } : meta;
}

function metaGoc(path, host = '') {
  // Firebase và người dùng đôi khi truy cập bản có dấu / ở cuối. React Router
  // vẫn render đúng, nhưng trước đây metadata lại rơi vào nhánh 404.
  path = path === '/' ? '/' : path.replace(/\/+$/, '');
  const canonical = `${host}${path}`;
  if (path === '/admin/mau') return { title: `Quản trị thư viện mẫu | ${SITE_NAME}`, description: 'Khu vực quản trị nội dung.', canonical, jsonLd: [], noindex: true };
  if (path === '/admin/nguon-von') return { title: `Quản trị nguồn vốn | ${SITE_NAME}`, description: 'Khu vực quản trị nội dung.', canonical, jsonLd: [], noindex: true };
  if (path === '/admin/chuong-trinh') return { title: `Quản trị chương trình | ${SITE_NAME}`, description: 'Khu vực quản trị nội dung.', canonical, jsonLd: [], noindex: true };
  if (path === '/admin/thong-ke') return { title: `Thống kê truy cập | ${SITE_NAME}`, description: 'Khu vực quản trị nội dung.', canonical, jsonLd: [], noindex: true };
  if (path === '/admin/nhan-xet-deck') return { title: `Quản trị nhận xét deck | ${SITE_NAME}`, description: 'Khu vực quản trị nội dung.', canonical, jsonLd: [], noindex: true };
  if (path === '/admin/spotlight') return { title: `Hồ sơ Startup Spotlight | ${SITE_NAME}`, description: 'Khu vực quản trị nội dung.', canonical, jsonLd: [], noindex: true };
  if (path === '/nhan-xet-deck') return {
    title: `Nhận xét pitch deck miễn phí | ${SITE_NAME}`,
    description: 'Gửi pitch deck dạng PDF, nhận nhận xét từng slide sau 15 đến 30 phút: việc nên sửa trước, câu chữ gợi ý viết lại và câu hỏi nhà đầu tư sẽ hỏi.',
    canonical,
    jsonLd: [{ '@context': 'https://schema.org', '@type': 'Service', name: 'Nhận xét pitch deck', serviceType: 'Nhận xét pitch deck gọi vốn',
      provider: { '@type': 'Organization', name: ORG_AUTHOR, url: host }, areaServed: 'VN', inLanguage: 'vi-VN', url: canonical,
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'VND' } }],
  };
  if (path === '/admin/video') return { title: `Quản trị video | ${SITE_NAME}`, description: 'Khu vực quản trị nội dung.', canonical, jsonLd: [], noindex: true };
  if (path === '/admin/playbook') return { title: `Quản trị Playbook | ${SITE_NAME}`, description: 'Khu vực quản trị nội dung.', canonical, jsonLd: [], noindex: true };
  const crumbs = (items) => ({ '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: items.map(([name, url], i) => ({ '@type': 'ListItem', position: i + 1, name, item: `${host}${url}` })) });
  const danhSachBai = (posts) => posts.map((p, i) => ({ '@type': 'ListItem', position: i + 1, name: p.title, url: `${host}/playbook/${p.slug}` }));

  if (path === '/video') return {
    title: `Video ngắn về gọi vốn: thuật ngữ, pitch deck, cap table | ${SITE_NAME}`,
    description: tomTat(`${VIDEOS.length ? `${VIDEOS.length} video` : 'Video'} dưới 1 phút giải thích thuật ngữ và việc chuẩn bị gọi vốn cho founder lần đầu: vesting, pre-money và post-money, pitch deck. Mỗi video dẫn tới bài đọc kỹ hơn trên fundraising.vn.`),
    canonical,
    jsonLd: [
      { '@context': 'https://schema.org', '@type': 'CollectionPage', name: 'Video ngắn về gọi vốn', inLanguage: 'vi-VN', url: canonical,
        mainEntity: { '@type': 'ItemList', itemListElement: VIDEOS.map((v, i) => ({ '@type': 'ListItem', position: i + 1, item: videoObject(v, host) })) } },
      crumbs([['Video ngắn', '/video']]),
    ],
  };
  if (path === '/playbook') return {
    title: `Fundraising Playbook cho founder Việt Nam | ${SITE_NAME}`,
    description: 'Tình huống gọi vốn mổ xẻ đến từng con số, góc nhìn của quỹ, hướng dẫn chuyên sâu kèm mẫu và cách tiếp cận nhà đầu tư cho startup Việt Nam.',
    canonical,
    jsonLd: [{ '@context': 'https://schema.org', '@type': 'CollectionPage', name: 'Fundraising Playbook', inLanguage: 'vi-VN', url: canonical,
      mainEntity: { '@type': 'ItemList', itemListElement: danhSachBai(PLAYBOOK_POSTS) } }],
  };
  if (path === '/playbook/gop-cau-chuyen') return {
    title: 'Gửi câu chuyện gọi vốn của bạn | Fundraising Playbook',
    description: 'Kể lại lần gọi vốn của bạn, thành hay chưa thành, để founder khác đi nhanh hơn. Có thể đăng ẩn danh; bài chỉ đăng khi bạn đồng ý bản cuối.',
    canonical,
    jsonLd: [crumbs([['Playbook', '/playbook'], ['Góp một câu chuyện', path]])],
  };
  const trackRoute = /^\/playbook\/([a-z-]+)$/.exec(path);
  if (trackRoute && TRACKS[trackRoute[1]]) {
    const track = TRACKS[trackRoute[1]];
    const posts = PLAYBOOK_POSTS.filter((p) => p.track === track.slug);
    return {
      title: `${track.label} | Fundraising Playbook`,
      description: tomTat(track.lead),
      canonical,
      noindex: posts.length === 0,
      jsonLd: [
        { '@context': 'https://schema.org', '@type': 'CollectionPage', name: track.label, inLanguage: 'vi-VN', url: canonical,
          mainEntity: { '@type': 'ItemList', itemListElement: danhSachBai(posts) } },
        crumbs([['Playbook', '/playbook'], [track.label, path]]),
      ],
    };
  }
  if (path === '/mau') return {
    title: `Mẫu Excel cho startup gọi vốn | ${SITE_NAME}`,
    description: 'Mẫu Excel mô hình tài chính, báo cáo và định giá cho startup gọi vốn, chia theo ngành: công thức sống, tiền Việt, tải miễn phí không cần tài khoản.',
    canonical,
    image: `${host}/og/mau.jpg`,
    jsonLd: [{ '@context': 'https://schema.org', '@type': 'ItemList', name: 'Mẫu Excel cho startup gọi vốn', inLanguage: 'vi-VN',
      itemListElement: TEMPLATES.map((t, i) => ({ '@type': 'ListItem', position: i + 1, name: t.title, url: `${host}/mau/${t.slug}` })) }],
  };
  if (path === '/mau/_ban-moi') {
    // Khung cho mẫu vừa xuất bản chưa có HTML tĩnh (firebase.json rewrite về đây).
    return { title: 'Mẫu Excel cho startup gọi vốn', description: 'Mẫu tải miễn phí cho startup gọi vốn.', canonical: '', jsonLd: [] };
  }
  const mauRoute = /^\/mau\/([^/]+)$/.exec(path);
  if (mauRoute) {
    const mau = templateBySlug(mauRoute[1]);
    if (mau) return {
      title: `${mau.title} (${mau.kind === 'dashboard' ? 'dùng' : 'tải'} miễn phí) | ${SITE_NAME}`,
      description: tomTat(mau.excerpt),
      canonical,
      ...(anhChiaSeMau(mau.slug) ? { image: `${host}${anhChiaSeMau(mau.slug)}` } : {}),
      jsonLd: [
        { '@context': 'https://schema.org', '@type': 'CreativeWork', name: mau.title, description: mau.excerpt, url: canonical,
          ...(mau.kind === 'dashboard' ? {} : { encodingFormat: DINH_DANG[mau.fileFormat || 'xlsx']?.contentType }),
          inLanguage: 'vi-VN', isAccessibleForFree: true, license: `${host}/dieu-khoan`,
          author: { '@type': 'Organization', name: ORG_AUTHOR, url: host } },
        crumbs([['Mẫu Excel', '/mau'], [mau.title, path]]),
      ],
    };
    return { title: `Không có mẫu này | ${SITE_NAME}`, description: 'Đường dẫn không tồn tại.', canonical: '', jsonLd: [], noindex: true };
  }
  const chuDe = /^\/playbook\/chu-de\/([^/]+)$/.exec(path);
  if (chuDe) {
    const cat = categoryBySlug(chuDe[1]);
    if (cat) {
      const posts = PLAYBOOK_POSTS.filter((p) => p.category === cat);
      return {
        title: `${cat} | Fundraising Playbook`,
        description: tomTat(`Các bài Playbook về ${cat.toLowerCase()} cho founder Việt Nam gọi vốn lần đầu: ${posts.map((p) => p.title).join('; ')}`),
        canonical,
        // Chủ đề chưa có bài thì không cho index — tránh trang mỏng.
        noindex: posts.length === 0,
        jsonLd: [
          { '@context': 'https://schema.org', '@type': 'CollectionPage', name: cat, inLanguage: 'vi-VN', url: canonical, mainEntity: { '@type': 'ItemList', itemListElement: danhSachBai(posts) } },
          crumbs([['Playbook', '/playbook'], [cat, path]]),
        ],
      };
    }
  }
  if (path === '/playbook/_ban-moi') {
    // Khung cho bài vừa xuất bản chưa có HTML tĩnh (firebase.json rewrite về đây).
    // Không canonical, không noindex: trang bài tự đặt head khi tải xong.
    return { title: 'Fundraising Playbook', description: 'Tình huống gọi vốn thực tế và hướng dẫn chuẩn bị hồ sơ cho founder Việt Nam.', canonical: '', jsonLd: [], ogType: 'article' };
  }
  const playbookRoute = /^\/playbook\/([^/]+)$/.exec(path);
  if (playbookRoute) {
    const post = playbookBySlug(playbookRoute[1]);
    if (post) {
      // Ảnh 1200×630: bản tải lên từ trang quản trị, nếu không có thì bản
      // scripts/gen-og.mjs vẽ sẵn lúc build.
      const image = post.ogImage || `${host}/og/playbook/${post.slug}.jpg`;
      const author = post.author === ORG_AUTHOR
        ? { '@type': 'Organization', name: ORG_AUTHOR, url: host }
        : { '@type': 'Person', name: post.author, ...(post.authorRole ? { jobTitle: post.authorRole } : {}) };
      return {
        title: `${post.seoTitle || post.title} | Fundraising Playbook`, description: tomTat(post.seoDescription || post.excerpt), canonical,
        image, ogType: 'article',
        jsonLd: [
          { '@context': 'https://schema.org', '@type': 'Article', headline: post.title, description: post.excerpt,
            image: [image],
            datePublished: post.publishedAt, dateModified: post.updatedAt || post.publishedAt, author,
            publisher: { '@type': 'Organization', name: ORG_AUTHOR, url: host, logo: { '@type': 'ImageObject', url: `${host}/apple-touch-icon.png` } },
            articleSection: post.category, inLanguage: 'vi-VN', mainEntityOfPage: canonical },
          crumbs([['Playbook', '/playbook'], [post.category, `/playbook/chu-de/${categorySlug(post.category)}`], [post.title, path]]),
        ],
      };
    }
    return { title: `Không có bài này | ${SITE_NAME}`, description: 'Đường dẫn không tồn tại.', canonical: '', jsonLd: [], noindex: true };
  }
  const laBanChuanBi = path === '/ban-chuan-bi' || path.startsWith('/ban-chuan-bi/');
  if (path === '/nguon-von' || laBanChuanBi) {
    return {title: `${path === '/nguon-von' ? 'Nguồn vốn cho startup' : 'Bản chuẩn bị của tôi'} | ${SITE_NAME}`,
      description: 'Khám phá nguồn vốn, lưu bản nháp và tiếp tục chuẩn bị gọi vốn.', canonical, jsonLd: [],
      ...(path === '/nguon-von' ? { image: `${host}/og/nguon-von.jpg` } : {}), noindex: laBanChuanBi};
  }

  const fundRoute = /^\/nguon-von\/([^/]+)$/.exec(path);
  if (fundRoute) {
    const fund = FUNDS.find((f) => f.slug === fundRoute[1] && f.publish?.status === 'PUBLISHED' && !f.isExample);
    if (fund) return {
      title: `${fund.name}: nguồn vốn cho startup | ${SITE_NAME}`,
      description: tomTat(fund.content?.summary || `Thông tin, điều kiện và nguồn kiểm chứng về ${fund.name}.`),
      canonical,
      ...(anhChiaSeQuy(fund.slug) ? { image: `${host}${anhChiaSeQuy(fund.slug)}` } : {}),
      jsonLd: [
        { '@context': 'https://schema.org', '@type': 'Organization', name: fund.name, url: fund.website, description: fund.content?.summary },
        crumbs([['Nguồn vốn', '/nguon-von'], [fund.name, path]]),
      ],
    };
  }

  // Trang gọn để chia sẻ. Trùng nội dung trang chủ nên canonical về "/" và noindex.
  if (path === '/bat-dau') {
    return {
      title: `Gọi vốn lần đầu? Bắt đầu từ đây | ${SITE_NAME}`,
      description: 'Mỗi câu hỏi nhà đầu tư sẽ hỏi là một phần của hồ sơ gọi vốn. Chấm mức sẵn sàng trong 3 phút, miễn phí, không cần tài khoản.',
      canonical: `${host}/`,
      image: `${host}/og/trang-chu.jpg`,
      jsonLd: [],
      noindex: true,
    };
  }

  if (path === '/') {
    return {
      title: `Chuẩn bị gọi vốn lần đầu | ${SITE_NAME}`,
      description:
        `${SO_CONG_CU} công cụ miễn phí cho vòng angel và pre-seed: tính runway, chia cổ `
        + 'phần đồng sáng lập, mô phỏng SAFE và rà soát hồ sơ trước khi gặp nhà đầu tư.',
      canonical,
      // Vẽ lúc build (scripts/gen-og.mjs) với số liệu hiện tại.
      image: `${host}/og/trang-chu.jpg`,
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Công cụ chuẩn bị gọi vốn cho startup giai đoạn đầu',
          itemListElement: TOOLS.map((t, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: t.title,
            url: `${host}/cong-cu/${t.slug}`,
          })),
        },
      ],
    };
  }

  if (path === '/cong-cu') {
    return {
      // Không thêm hậu tố SITE_NAME: tên trang trùng luôn với tên site, ghép
      // vào thành "Bộ công cụ gọi vốn — Bộ công cụ gọi vốn".
      title: `${SO_CONG_CU} công cụ chuẩn bị gọi vốn | ${SITE_NAME}`,
      description: `${SO_CONG_CU} công cụ miễn phí giúp startup tính runway, cổ phần, SAFE và chuẩn bị hồ sơ trước vòng angel hoặc pre-seed.`,
      canonical,
      jsonLd: [{
        '@context': 'https://schema.org', '@type': 'ItemList',
        name: 'Công cụ chuẩn bị gọi vốn cho startup giai đoạn đầu',
        itemListElement: TOOLS.map((t, i) => ({ '@type': 'ListItem', position: i + 1, name: t.title, url: `${host}/cong-cu/${t.slug}` })),
      }],
    };
  }

  const m = /^\/cong-cu\/([^/]+)$/.exec(path);
  if (m) {
    const tool = toolBySlug(m[1]);
    if (tool) {
      const content = contentFor(tool);
      const jsonLd = [
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: tool.title,
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          inLanguage: 'vi-VN',
          description: tomTat(content.intro),
          url: canonical,
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'VND' },
        },
        crumbs([['Công cụ', '/cong-cu'], [tool.title, path]]),
      ];
      if (content.faq.length > 0) {
        jsonLd.push({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: content.faq.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        });
      }
      return {
        title: `${tool.title} | ${SITE_NAME}`,
        description: tomTat(content.intro),
        canonical,
        ...(anhChiaSeCongCu(tool.slug) ? { image: `${host}${anhChiaSeCongCu(tool.slug)}` } : {}),
        jsonLd,
      };
    }
  }

  if (path === '/chuong-trinh') {
    return {
      title: `Chương trình và quỹ tài trợ cho startup | ${SITE_NAME}`,
      description:
        'Danh sách chương trình tăng tốc, cuộc thi, tài trợ nghiên cứu và tín dụng '
        + 'hạ tầng mà startup Việt Nam nộp được. Phần lớn không lấy cổ phần.',
      canonical,
      image: `${host}/og/chuong-trinh.jpg`,
      jsonLd: [{
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Chương trình và quỹ tài trợ cho startup Việt Nam',
        inLanguage: 'vi-VN',
        itemListElement: PROGRAMS
          .filter((p) => p.publish?.status === 'PUBLISHED')
          .map((p, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: p.name,
            description: p.content?.summary,
            url: p.process?.applyUrl,
          })),
      }],
    };
  }
  if (path === '/lo-trinh') return {
    title: `Lộ trình gọi vốn 5 trạm cho founder lần đầu | ${SITE_NAME}`,
    description: 'Năm trạm từ runway tới email đầu tiên gửi quỹ: tiền, mô hình tài chính, cổ phần và định giá, pitch deck, tìm quỹ. Mỗi trạm kèm công cụ, bài đọc và mẫu.',
    canonical,
    jsonLd: [{ '@context': 'https://schema.org', '@type': 'ItemList', name: 'Lộ trình gọi vốn 5 trạm', inLanguage: 'vi-VN', url: canonical,
      itemListElement: TRAM.map((t, i) => ({ '@type': 'ListItem', position: i + 1, name: t.ten, url: `${canonical}#${t.id}` })) }],
  };
  if (path === '/huong-dan/goi-von-lan-dau') return {
    title: `Chuẩn bị gọi vốn lần đầu: bắt đầu từ đâu? | ${SITE_NAME}`,
    description: 'Hướng dẫn thực hành cho startup lần đầu gọi vốn: tính runway, xác định cột mốc, chuẩn bị bằng chứng và chọn nguồn vốn phù hợp.',
    canonical,
    jsonLd: [{ '@context': 'https://schema.org', '@type': 'Article', headline: 'Chuẩn bị gọi vốn lần đầu: bắt đầu từ đâu?', inLanguage: 'vi-VN', mainEntityOfPage: canonical }],
  };

  if (path === '/thuat-ngu') {
    return {
      title: `Từ điển gọi vốn | ${SITE_NAME}`,
      description:
        'Giải thích các thuật ngữ gọi vốn hay gặp bằng tiếng Việt, kèm ví dụ bằng '
        + 'tiền Việt: SAFE, vesting, pha loãng, ESOP, runway, CAC và nhiều hơn nữa.',
      canonical,
      image: `${host}/og/thuat-ngu.jpg`,
      jsonLd: [{
        '@context': 'https://schema.org',
        '@type': 'DefinedTermSet',
        name: 'Từ điển gọi vốn cho startup Việt Nam',
        inLanguage: 'vi-VN',
        url: canonical,
        hasDefinedTerm: DA_XUAT_BAN.map((x) => ({
          '@type': 'DefinedTerm',
          name: x.term,
          description: x.shortDef,
          url: `${host}/thuat-ngu/${x.slug}`,
        })),
      }],
    };
  }

  const tn = /^\/thuat-ngu\/([^/]+)$/.exec(path);
  if (tn) {
    const muc = tuDienTheoSlug(tn[1]);
    const nd = muc && noiDungTheoSlug(tn[1]);
    if (muc && nd) {
      const jsonLd = [{
        '@context': 'https://schema.org',
        '@type': 'DefinedTerm',
        name: muc.term,
        description: muc.shortDef,
        inDefinedTermSet: `${host}/thuat-ngu`,
        url: canonical,
        inLanguage: 'vi-VN',
      }, crumbs([['Từ điển', '/thuat-ngu'], [muc.term, path]])];
      if (nd.faq?.length) {
        jsonLd.push({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: nd.faq.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: boLienKet(f.a, (s) => tuDienTheoSlug(s)?.term) },
          })),
        });
      }
      return {
        // Kèm tên tiếng Anh khi có, vì founder hay gõ cả hai kiểu: "cap table
        // là gì" và "bảng cổ phần là gì" đều phải khớp tiêu đề.
        // Mục đặt tên dạng câu ("Gọi bao nhiêu là hợp lý") không thêm "là gì".
        title: SEO_THUAT_NGU[muc.slug]?.title ? `${SEO_THUAT_NGU[muc.slug].title} | ${SITE_NAME}`
          : /^(Vì sao|Chọn|Gọi bao nhiêu|Bị )/.test(muc.term)
          ? `${muc.term} | ${SITE_NAME}`
          : `${tenTieuDe(muc)} là gì | ${SITE_NAME}`,
        description: SEO_THUAT_NGU[muc.slug]?.description
          || tomTat(`${muc.shortDef} ${boLienKet(nd.body[0]?.p?.[0] || '', (s) => tuDienTheoSlug(s)?.term)}`),
        canonical,
        ...(anhChiaSeThuatNgu(muc.slug) ? { image: `${host}${anhChiaSeThuatNgu(muc.slug)}` } : {}),
        jsonLd,
      };
    }
  }

  if (path === '/gioi-thieu') {
    return {
      title: `Giới thiệu | ${SITE_NAME}`,
      description:
        'Fundraising.vn là bộ công cụ tiếng Việt giúp founder lần đầu hiểu các con số, '
        + 'rà soát hồ sơ và tìm nguồn vốn phù hợp. Miễn phí, không cần tài khoản.',
      canonical,
      image: `${host}/og/gioi-thieu.jpg`,
      jsonLd: [],
    };
  }

  const trangPhapLy = legalBySlug(path.replace(/^\//, ''));
  if (trangPhapLy) {
    return {
      title: `${trangPhapLy.title} | ${SITE_NAME}`,
      description: tomTat(trangPhapLy.tomTat),
      canonical,
      jsonLd: [],
    };
  }

  return {
    title: `Không có trang này | ${SITE_NAME}`,
    description: 'Đường dẫn không tồn tại.',
    canonical,
    jsonLd: [],
    noindex: true,
  };
}

/** Danh sách đường dẫn cần render sẵn thành HTML tĩnh. */
export function allRoutes() {
  return [
    '/',
    '/bat-dau',
    '/lo-trinh',
    '/cong-cu',
    '/nguon-von',
    ...FUNDS.filter((f) => f.publish?.status === 'PUBLISHED' && !f.isExample).map((f) => `/nguon-von/${f.slug}`),
    '/ban-chuan-bi',
    // Mỗi mục của Bản chuẩn bị là một đường dẫn riêng: cần HTML tĩnh để mở
    // thẳng hoặc làm mới trang không rơi vào 404.
    ...['tong-quan', 'ho-so', 'so-lieu', 'ban-mot-trang', 'nguon-von', 'tai-khoan'].map((m) => `/ban-chuan-bi/${m}`),
    ...TOOLS.map((t) => `/cong-cu/${t.slug}`),
    ...LEGAL_PAGES.map((p) => `/${p.slug}`),
    '/chuong-trinh',
    '/gioi-thieu',
    '/playbook',
    ...PLAYBOOK_CATEGORIES.map((c) => `/playbook/chu-de/${categorySlug(c)}`),
    ...PLAYBOOK_POSTS.map((post) => `/playbook/${post.slug}`),
    '/playbook/gop-cau-chuyen',
    ...Object.keys(TRACKS).map((t) => `/playbook/${t}`),
    '/mau',
    ...TEMPLATES.map((t) => `/mau/${t.slug}`),
    '/mau/_ban-moi',
    '/admin/mau',
    '/admin/chuong-trinh',
    '/admin/nguon-von',
    '/admin/thong-ke',
    '/admin/nhan-xet-deck',
    '/admin/spotlight',
    '/nhan-xet-deck',
    '/playbook/_ban-moi',
    '/admin/playbook',
    '/video',
    '/admin/video',
    '/huong-dan/goi-von-lan-dau',
    '/thuat-ngu',
    ...DA_XUAT_BAN.map((x) => `/thuat-ngu/${x.slug}`),
  ];
}
